import { GridRowId } from '@mui/x-data-grid/models/gridRows';
import { GridApiCommunity } from '@mui/x-data-grid/models/api/gridApiCommunity';
import { orderDetailsState as orderDetailsStateCreate } from '../../../create/states/orderDetails/orderDetails.state.ts';
import { orderDetailsState as orderDetailsStateEdit } from '../../../edit/states/orderDetails/orderDetails.state.ts';
import { offerDetailsState } from '../../../../offer/states/offerDetails/offerDetails.state.ts';
import {
  insertRowAndFocus,
  shouldHorizontalScroll,
} from '../../../../../shared/components/table/functions/sharedFunc.ts';
import { storageHelper } from '../../../../../shared/helpers/storage';

export class EnterKeyNavigation {
  private fields: Array<string> = [];
  private readonly type: Type = 'create';
  constructor(args: { sessionFields?: Array<string>; type: Type }) {
    const { sessionFields, type } = args;
    this.fields = sessionFields || [];
    this.type = type;
  }
  setKeyCombination(fields: Array<string>): void {
    this.fields = fields;
    storageHelper.local.setItem('order_offer.keyCombination', fields);
  }
  getKeyCombination(): Array<string> {
    return this.fields;
  }
  private getState() {
    if (this.type === 'offer') return offerDetailsState;
    return this.type === 'create' ? orderDetailsStateCreate : orderDetailsStateEdit;
  }
  /**
   * Moves focus to the next cell of the Enter sequence.
   *
   * Past the last field of the last row a brand-new row is needed. It is put
   * into the grid and focused here, synchronously, in the same keydown: the
   * state's `rows` prop only reaches the grid an effect and a render later,
   * and any keystroke arriving before the new cell held DOM focus either
   * edited the row the user had just finished or had to be dropped — the
   * "digits not registered" of WA-3671/WA-3803.
   */
  handleEnterKey(field: string, rowId: GridRowId, api: GridApiCommunity): void {
    const {
      getAllColumns,
      getSortedRows,
      scroll,
      getRootDimensions,
      getScrollPosition,
      getColumn,
      getColumnPosition,
      setCellFocus,
    } = api;
    const isCurrentFieldInFields = !!this.fields.find((el) => el === field);
    const currentColumn = getColumn(field);
    const currentTemp = getAllColumns().reduce((acc: string[], item) => {
      if (
        (!isCurrentFieldInFields || this.fields.length === 0 || this.fields.includes(item.field)) &&
        (!currentColumn.editable || item.editable) &&
        item.field !== '__reorder__'
      )
        acc.push(item.field);
      return acc;
    }, []);

    const fieldIndex = currentTemp.findIndex((el) => el === field);
    const containerWidth = getRootDimensions()?.viewportInnerSize?.width || 0;
    const { left: scrollLeft } = getScrollPosition();
    if (fieldIndex === currentTemp.length - 1) {
      const filteredRows = getSortedRows().filter(
        (el) => !el.virtualPositionId && (this.type === 'offer' || el.isChangeable),
      );
      const rowIndex = filteredRows.findIndex((el) => el.id === rowId);
      const nextRow = filteredRows[rowIndex + 1];
      const { width: columnWidth = 0 } = getColumn(currentTemp[0]);
      const columnLeft = getColumnPosition(currentTemp[0]);
      const needToScroll = shouldHorizontalScroll({
        scrollLeft,
        columnLeft,
        columnWidth,
        containerWidth,
      });
      if (nextRow) {
        this.getState().pub.selectPosition(nextRow as any);
        setCellFocus(nextRow.id as string, currentTemp[0]);
        if (needToScroll) {
          scroll({ left: columnLeft });
        }
      } else {
        // Enter navigation runs after the cell has already committed and left
        // edit mode, so the 50 ms grace period the toolbar "+" button needs
        // is skipped and the state takes the row synchronously.
        const position = this.getState().pub.addPosition({ type: 'addPosition', immediate: true });
        if (position) insertRowAndFocus(api, position, currentTemp[0]);
        if (needToScroll) {
          scroll({ left: columnLeft });
        }
      }
    } else {
      const nextField = currentTemp[fieldIndex + 1];
      const { width: columnWidth = 0 } = getColumn(nextField);
      const columnLeft = getColumnPosition(nextField);
      const needToScroll = shouldHorizontalScroll({
        scrollLeft,
        columnLeft,
        columnWidth,
        containerWidth,
      });
      if (needToScroll) {
        scroll({ left: columnLeft });
      }
      setCellFocus(rowId, nextField);
    }
  }
}

type Type = 'create' | 'edit' | 'offer';
